import type { ReactNode } from 'react'

import { clsx } from 'clsx'

import IconChevronDown from '@/assets/icons/icon-chevron-down.svg'
import IconInfo from '@/assets/icons/icon-info.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import type { PlaceholderVariant } from '@/modules/message/components/placeholder/variants'
import { formatDefaultTooltip } from '@/modules/message/components/placeholder/variants'
import { classNames } from '@/utils/helpers'

interface PlaceholderInserterProps {
  name: ReactNode
  variants: PlaceholderVariant[]
  toggleName?: ReactNode
  toggleTooltip?: ReactNode
  toggleValue?: boolean
  onToggle?: () => void
  onClick: (variant: PlaceholderVariant) => void
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  testId?: string
}

export const PlaceholderSelector = (props: PlaceholderInserterProps) => {
  const {
    open,
    onOpenChange,
    className,
    toggleName,
    toggleTooltip,
    onToggle,
    toggleValue,
    variants,
    name,
    onClick,
    testId,
  } = props

  return (
    <DropdownMenu
      open={open}
      onOpenChange={onOpenChange}
    >
      <DropdownMenuTrigger
        type="reset"
        className={classNames(
          'flex h-[32px] items-center gap-1 rounded-md border-[1px] bg-secondary-50 px-2 text-sm font-medium text-title dark:bg-secondary-600',
          className,
        )}
        testId={testId}
      >
        {name}
        <IconChevronDown className="ml-auto h-4 w-4 flex-shrink-0 text-title" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-[100] w-64"
        align="start"
        forceMount
      >
        {toggleName && (
          <>
            <DropdownMenuLabel className="group -m-2 flex items-center gap-2 rounded-md p-2 font-normal text-title">
              <input
                type="checkbox"
                className="accent-primary-500"
                checked={toggleValue}
                onChange={(event) => {
                  event.stopPropagation()
                  onToggle?.()
                }}
                data-testid={`${testId}-toggle-input`}
              />
              {toggleName}
              {toggleTooltip && (
                <Tooltip content={toggleTooltip}>
                  <IconInfo className="h-4 w-4" />
                </Tooltip>
              )}
            </DropdownMenuLabel>
            <div className="w-full border-b border-b-border pb-2 pt-2" />
          </>
        )}
        <DropdownMenuGroup
          className={clsx('flex flex-col gap-[16px]', {
            'mt-[20px]': toggleName,
          })}
        >
          {variants.map((variant, index) => {
            const { id, name, tooltip } = variant

            return (
              <Tooltip
                key={id}
                content={tooltip ?? formatDefaultTooltip(name)}
                contentClassName="z-[101]"
              >
                <DropdownMenuItem
                  className="group -m-2 justify-between rounded-md px-2 text-title hover:bg-primary-500/20 hover:text-primary-500"
                  onClick={() => onClick(variant)}
                  testId={`${testId}-item-${index}`}
                >
                  {name}
                </DropdownMenuItem>
              </Tooltip>
            )
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
